import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, Users, CheckCircle2, XCircle, Loader2, Video } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/ui/ToastProvider';
import { liveClassService } from '../../services/live-class.service';
import type { LiveClassSession } from '@edytech/shared/types';

const formatWhen = (s: LiveClassSession) =>
  new Date(s.startsAt).toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export const LiveClassDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data: liveClass, isLoading } = useQuery({
    queryKey: ['live-class', id],
    queryFn: () => liveClassService.getById(id),
    // Refresh session status (Scheduled → Live now → Ended) and registration
    // availability without a manual reload.
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  const registerMutation = useMutation({
    mutationFn: (sessionId: number) => liveClassService.register(sessionId),
    onSuccess: () => {
      showToast('Registered! Check your email for the calendar invite.', 'success');
      queryClient.invalidateQueries({ queryKey: ['live-class', id] });
    },
    onError: (e: Error) => showToast(e.message || 'Registration failed', 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: (sessionId: number) => liveClassService.cancelRegistration(sessionId),
    onSuccess: () => {
      showToast('Registration cancelled.', 'success');
      queryClient.invalidateQueries({ queryKey: ['live-class', id] });
    },
    onError: (e: Error) => showToast(e.message || 'Cancel failed', 'error'),
  });

  if (isLoading) {
    return <div className="container mx-auto px-4 py-16 text-center">Loading live class...</div>;
  }

  if (!liveClass) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p>This live class is not available.</p>
        <Link to="/live-classes" className="mt-4 inline-block text-primary hover:underline">
          ← Back to live classes
        </Link>
      </div>
    );
  }

  const sessions = liveClass.sessions ?? [];

  return (
    <div className="container mx-auto px-4 py-12">
      <Link to="/live-classes" className="text-sm text-primary hover:underline">
        ← All live classes
      </Link>

      <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h1 className="text-3xl font-bold">{liveClass.title}</h1>
          {liveClass.instructorName && (
            <p className="mt-2 flex items-center gap-2 text-[rgb(var(--light-secondary))]">
              <Users className="h-4 w-4" /> Instructor: {liveClass.instructorName}
            </p>
          )}
          {liveClass.description && (
            <p className="mt-4 whitespace-pre-line text-[rgb(var(--light-secondary))]">
              {liveClass.description}
            </p>
          )}
          {liveClass.tags && liveClass.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {liveClass.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-[rgb(var(--border))] px-3 py-1 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <Card className="h-fit p-6">
          <h3 className="font-semibold">Class overview</h3>
          <ul className="mt-3 space-y-2 text-sm text-[rgb(var(--light-secondary))]">
            <li className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {sessions.length} scheduled session{sessions.length === 1 ? '' : 's'}
            </li>
            <li className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Capacity: {liveClass.capacity}
            </li>
          </ul>
          {!isAuthenticated && (
            <p className="mt-4 rounded-lg border border-dashed border-[rgb(var(--border))] p-3 text-xs text-[rgb(var(--light-secondary))]">
              Sign in to register for a session.
            </p>
          )}
        </Card>
      </div>

      <h2 className="mt-10 text-xl font-bold">Sessions</h2>
      <div className="mt-4 space-y-4">
        {sessions.length === 0 && (
          <p className="text-[rgb(var(--light-secondary))]">No sessions scheduled yet.</p>
        )}
        {sessions.map((session) => {
          const registered = session.registration?.status === 'REGISTERED';
          const statusLabel: Record<string, string> = {
            SCHEDULED: 'Scheduled',
            ONGOING: 'Live now',
            ENDED: 'Ended',
            CANCELLED: 'Cancelled',
          };
          return (
            <Card key={session.id} className="p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{session.title || liveClass.title}</h3>
                    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                      {statusLabel[session.status] ?? session.status}
                    </span>
                  </div>
                  <p className="mt-1 flex items-center gap-2 text-sm text-[rgb(var(--light-secondary))]">
                    <Clock className="h-4 w-4" /> {formatWhen(session)} ({session.timezone})
                  </p>
                  <p className="mt-1 text-xs text-[rgb(var(--light-secondary))]">
                    {session.registeredCount ?? 0} / {session.capacity ?? liveClass.capacity} registered
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {session.status !== 'CANCELLED' &&
                    session.status !== 'ENDED' &&
                    (registered ||
                      (user &&
                        (String(user.role) === 'instructor' || String(user.role) === 'admin'))) && (
                      <Link to={`/live-classes/${id}/sessions/${session.id}/room`}>
                        <Button size="sm">
                          <Video className="mr-1 h-4 w-4" /> Join
                        </Button>
                      </Link>
                    )}
                  {registered ? (
                    <>
                      <span className="flex items-center gap-1 text-sm font-medium text-green-600">
                        <CheckCircle2 className="h-4 w-4" /> Registered
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={cancelMutation.isPending}
                        onClick={() => cancelMutation.mutate(session.id)}
                      >
                        {cancelMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      disabled={!isAuthenticated || !session.canRegister || registerMutation.isPending}
                      onClick={() => registerMutation.mutate(session.id)}
                    >
                      {registerMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {session.canRegister ? 'Register' : 'Registration closed'}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
